/**
 * The shared product row.
 *
 * A scroll-snapping track with arrows, used by related products and the
 * featured collection. Scrolling is native — the arrows only call
 * `scrollBy`, so touch, trackpad, keyboard and the scrollbar all work
 * without being reimplemented.
 *
 * Nothing here needs a re-scan after `product-recommendations` swaps its
 * markup: a custom element upgrades when it is inserted into the document, so
 * the replacement track initialises itself.
 */

(() => {
  if (customElements.get('product-carousel')) return;

  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-row-track]');
      this.prev = this.querySelector('[data-row-prev]');
      this.next = this.querySelector('[data-row-next]');
      if (!this.track) return;

      this.prev?.addEventListener('click', () => this.page(-1));
      this.next?.addEventListener('click', () => this.page(1));
      this.track.addEventListener('scroll', () => this.sync(), { passive: true });

      // The card count is fixed but the cards-per-view is not, so the same row
      // can need arrows at one width and not at another.
      this.observer = new ResizeObserver(() => this.sync());
      this.observer.observe(this.track);

      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    /** Moves by a viewport of the track, so nothing is skipped past. */
    page(direction) {
      this.track.scrollBy({
        left: direction * this.track.clientWidth,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    sync() {
      // A one-pixel tolerance: fractional layout widths mean an unscrolled
      // track can report a scrollLeft of 0.5, and a fully scrolled one can
      // land a fraction short of its own maximum.
      const max = this.track.scrollWidth - this.track.clientWidth;
      const scrollable = max > 1;

      this.classList.toggle('is-scrollable', scrollable);

      if (this.prev) this.prev.hidden = !scrollable || this.track.scrollLeft <= 1;
      if (this.next) this.next.hidden = !scrollable || this.track.scrollLeft >= max - 1;
    }
  }

  customElements.define('product-carousel', ProductCarousel);
})();
